import {Tooltip} from "antd";
import dayjs from "dayjs";
import className from "classnames/bind";
import FontAwesomeIcon from "../FontAwesome";
import {useGetMySubscriptionQuery} from "~/reduxs/api/subscriptionApiSlice";
import style from "./ProjectQuota.module.scss";

const cn = className.bind(style);

const nf = (n) => Number(n || 0).toLocaleString('vi-VN');

/** Màu thanh tiến trình theo % đã dùng (xanh → cam → đỏ khi sắp hết). */
const quotaColor = (pct) => (pct >= 90 ? '#dc2626' : pct >= 70 ? '#f59e0b' : '#16a34a');

/** Metadata cho từng chỉ số hạn mức (khớp key trả về từ `subscription/me`). */
const QUOTA_META = {
	ai_tokens:          {icon: 'fa-light fa-wand-magic-sparkles',    label: 'Token AI',          cycle: 'tháng'},
	url_checks:         {icon: 'fa-light fa-link',                   label: 'URL kiểm tra',      cycle: 'tuần'},
	keyword_lookups:    {icon: 'fa-light fa-magnifying-glass-dollar', label: 'Tra volume',       cycle: 'tháng'},
	serp_checks:        {icon: 'fa-light fa-ranking-star',           label: 'Kiểm tra thứ hạng', cycle: 'tháng'},
	backlink_syncs:     {icon: 'fa-light fa-link',                   label: 'Đồng bộ backlink',  cycle: 'tháng'},
	competitor_lookups: {icon: 'fa-light fa-users-viewfinder',       label: 'Phân tích đối thủ', cycle: 'tháng'},
	auto_content_posts: {icon: 'fa-light fa-feather-pointed',        label: 'Bài viết AI',       cycle: 'tháng'},
};

/**
 * Chỉ số hạn mức liên quan tới từng tool. Chỉ liệt kê tool THỰC SỰ tiêu hạn mức
 * đo được (url_checks/token/DataForSEO); tool chỉ đọc (crawler/technical/analytics)
 * không có → strip tự ẩn.
 */
const TOOL_QUOTAS = {
	overview:   ['url_checks', 'ai_tokens'],
	gsc:        ['url_checks'],
	keywords:   ['keyword_lookups', 'url_checks'],
	rank:       ['serp_checks'],
	backlinks:  ['backlink_syncs'],
	competitor: ['competitor_lookups', 'url_checks'],
	ai:         ['ai_tokens'],
	content:    ['auto_content_posts', 'ai_tokens'],
};

/** 1 ô hạn mức gọn: nhãn + số còn lại/hạn mức + thanh tiến trình mảnh. renew=true: hạn mức theo chu kỳ gói (làm mới khi gia hạn). */
function QuotaChip({metric, data, ownKey, renew}) {
	const meta = QUOTA_META[metric];
	if (!meta || !data) return null;

	const limit = Number(data.limit || 0);
	const used = Number(data.used || 0);
	const remaining = data.remaining != null ? Number(data.remaining) : Math.max(0, limit - used);

	// Token AI khi gói bắt buộc khoá riêng → không tính hạn mức chung.
	if (metric === 'ai_tokens' && ownKey) {
		return (
			<div className={cn('quota')}>
				<span className={cn('quota-head')}><FontAwesomeIcon icon="fa-light fa-key" /> {meta.label}</span>
				<span className={cn('quota-val', 'is-special')}>Khoá AI riêng</span>
			</div>
		);
	}

	// Gói không có tính năng này (hạn mức 0) → chip mờ nhắc nâng cấp.
	if (limit <= 0) {
		return (
			<Tooltip title="Không có trong gói của bạn — nâng cấp để sử dụng.">
				<div className={cn('quota', 'is-off')}>
					<span className={cn('quota-head')}><FontAwesomeIcon icon={meta.icon} /> {meta.label}</span>
					<span className={cn('quota-val', 'is-off')}>Nâng cấp để dùng</span>
				</div>
			</Tooltip>
		);
	}

	// Hạn mức có phần cộng thêm từ Gói Extra (limit đã là tổng gói + Extra).
	const extra = Number(data.extra || 0);

	const pct = Math.min(100, Math.round((used / limit) * 100));
	// Kỳ "tháng" giờ là CHU KỲ GÓI với gói trả phí (làm mới khi gia hạn, không theo lịch).
	const cycleWord = renew && meta.cycle === 'tháng' ? 'chu kỳ gói' : meta.cycle;
	const tip = `Đã dùng ${nf(used)} / ${nf(limit)} mỗi ${cycleWord}`
		+ (extra > 0 ? ` (gồm ${nf(extra)} từ Gói Extra — hết hạn cuối ${renew ? 'chu kỳ' : 'tháng'})` : '')
		+ (data.reset_at
			? (renew && meta.cycle === 'tháng' ? ' · làm mới khi gia hạn (hạn ' + dayjs(data.reset_at).format('DD/MM') + ')' : ' · làm mới ' + dayjs(data.reset_at).format('DD/MM'))
			: renew && meta.cycle === 'tháng' ? ' · làm mới khi gia hạn gói' : '');

	return (
		<Tooltip title={tip}>
			<div className={cn('quota')}>
				<span className={cn('quota-head')}>
					<FontAwesomeIcon icon={meta.icon} /> {meta.label}
					{extra > 0 && <span className={cn('quota-extra')}>+{nf(extra)} Extra</span>}
				</span>
				<span className={cn('quota-val')}>
					{nf(remaining)} <span className={cn('quota-of')}>còn lại / {nf(limit)}</span>
				</span>
				<div className={cn('quota-track')}>
					<div className={cn('quota-fill')} style={{width: `${pct}%`, background: quotaColor(pct)}} />
				</div>
			</div>
		</Tooltip>
	);
}

/**
 * Hạn mức còn lại của user, hiển thị gọn trong `actions` của PageHeader ở các trang
 * tool trong dự án — chỉ các chỉ số liên quan tới `tool` đang mở. Tài khoản quản trị
 * (root) không có hạn mức → không render gì.
 *
 * Props:
 *  - tool : key tool ('overview'|'gsc'|'keywords'|'rank'|'backlinks'|'competitor'|'ai').
 */
function ProjectQuota({tool}) {
	// refetch khi cache quá 60s: hạn mức có thể đổi NGOÀI client này (admin duyệt Gói
	// Extra / cộng token) — mutation của chính user không invalidate được các thay đổi đó.
	const {data: sub} = useGetMySubscriptionQuery(undefined, {refetchOnMountOrArgChange: 60});

	if (!sub || sub.is_root) return null;

	const keys = TOOL_QUOTAS[tool] || [];
	if (keys.length === 0) return null;

	const ownKey = Boolean(sub.require_own_ai_key);
	const renew = sub.billing?.cycle === 'purchase';

	return (
		<div className={cn('quotas')}>
			{keys.map((k) => (
				<QuotaChip key={k} metric={k} data={sub[k]} ownKey={ownKey} renew={renew} />
			))}
		</div>
	);
}

export default ProjectQuota;
